import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatTime } from '@/lib/format';
import type { BoardItem } from '@/lib/boardItems';
import type { JobConflict } from '@/lib/conflicts';
import { ConflictBadge, describeConflicts } from './ConflictBadge';
import type { JobDragBinding } from './dragBinding';

/**
 * One job, as it appears on the board or in the unassigned rail (M1-09),
 * draggable and keyboard-operable since M1-11.
 *
 * A real `<button>`. It is the drag handle for a pointer and the trigger for
 * the move dialog from a keyboard, and making it a button means Enter and
 * Space activate it, it takes a tab stop, and it announces itself as something
 * that does a thing — all without a single `role` or `onKeyDown` here. The
 * pointer handlers distinguish a press that moved from one that did not, so
 * clicking still activates (see `useJobDrag`).
 *
 * `touch-action: none` is what makes this work on a phone: without it the
 * browser claims the gesture for scrolling before any pointer handler runs.
 */
export function JobCard({
  job,
  timeZone,
  compact = false,
  className,
  isDragging = false,
  handlers,
  conflicts,
}: {
  job: BoardItem;
  timeZone: string;
  conflicts?: JobConflict[];
  /** The rail's variant: no time to show, and less room to show it in. */
  compact?: boolean;
  className?: string;
  isDragging?: boolean;
  /** Omitted where the card is only being displayed — the drag preview. */
  handlers?: ReturnType<JobDragBinding['handlersFor']>;
}) {
  const { t } = useTranslation();
  const isUrgent = job.priority === 'Emergency' || job.priority === 'High';
  // One day of a multi-day job (M2-52, Kyle 2026-09-25): said on the card and
  // in its label, so moving Tuesday is visibly moving Tuesday, not the job.
  const visitLabel =
    job.visitCount > 1
      ? t('dispatch.visitOf', { number: job.visitNumber, total: job.visitCount })
      : null;
  const interactive = handlers !== undefined;
  // M2-21 (Kyle's 11): the newest appointment email about this visit bounced
  // or was dropped — said in words, on the card and in its label, never as a
  // colour alone. Office only: the API sends everyone else false.
  const bounced = job.emailBounced ? t('dispatch.emailBounced') : null;

  const Element = interactive ? 'button' : 'div';

  return (
    <Element
      type={interactive ? 'button' : undefined}
      {...(handlers ?? {})}
      // The badge below is inside a button that HAS an aria-label, so its
      // text is not read unless it is folded in here as well.
      aria-label={
        interactive
          ? [
              t('dispatch.move.cardLabel', { customer: job.customerName }),
              visitLabel,
              bounced,
              describeConflicts(conflicts, t),
            ]
              .filter(Boolean)
              .join('. ')
          : undefined
      }
      className={cn(
        'flex h-full w-full flex-col gap-0.5 overflow-hidden rounded-md border bg-[var(--color-card)] px-2 py-1 text-left text-xs',
        interactive &&
          'cursor-grab touch-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none',
        // Left edge marks urgency, but never on its own — the badge below says
        // "Emergency" in words for anyone who cannot tell this border from the
        // ordinary one.
        isUrgent && 'border-l-4 border-l-[var(--color-destructive)]',
        // A clash outlines the whole card, which reads differently from the
        // urgency stripe on its left edge. Never on its own — the badge below
        // says what is wrong in words.
        conflicts && conflicts.length > 0 && 'border-[var(--color-destructive)]',
        // The original stays put and dimmed while a copy follows the pointer,
        // so it is obvious which card is moving and where it came from.
        isDragging && 'pointer-events-none opacity-40',
        className,
      )}
      data-testid="job-card"
    >
      {/* Spans, not paragraphs: a <button> may only contain phrasing
          content, and this card is a button. */}
      {!compact && job.scheduledStart ? (
        <span className="block font-medium">
          <time dateTime={job.scheduledStart}>{formatTime(job.scheduledStart, timeZone)}</time>
          {job.scheduledEnd ? (
            <>
              {' – '}
              <time dateTime={job.scheduledEnd}>{formatTime(job.scheduledEnd, timeZone)}</time>
            </>
          ) : null}
        </span>
      ) : null}

      <span className="block truncate font-medium">{job.customerName}</span>
      <span className="block truncate text-[var(--color-muted-foreground)]">
        {job.propertyUnit ? `${job.propertyLine1}, ${job.propertyUnit}` : job.propertyLine1}
      </span>
      <span className="block truncate text-[var(--color-muted-foreground)]">{job.description}</span>

      {conflicts && conflicts.length > 0 ? <ConflictBadge conflicts={conflicts} /> : null}

      <span className="mt-auto flex flex-wrap items-center gap-1 pt-1">
        <span className="rounded-full bg-[var(--color-muted)] px-1.5 py-0.5 text-[0.625rem] font-medium">
          {t(`common.jobStatus.${job.status}`, { defaultValue: job.status })}
        </span>
        {visitLabel ? (
          <span className="rounded-full border px-1.5 py-0.5 text-[0.625rem] font-medium" data-testid="visit-chip">
            {visitLabel}
          </span>
        ) : null}
        {bounced ? (
          <span
            className="rounded-full border border-[var(--color-destructive)] px-1.5 py-0.5 text-[0.625rem] font-medium text-[var(--color-destructive)]"
            data-testid="bounced-chip"
          >
            {bounced}
          </span>
        ) : null}
        {isUrgent ? (
          <span className="rounded-full border border-[var(--color-destructive)] px-1.5 py-0.5 text-[0.625rem] font-medium text-[var(--color-destructive)]">
            {t(`common.jobPriority.${job.priority}`, { defaultValue: job.priority })}
          </span>
        ) : null}
      </span>
    </Element>
  );
}
